import { HttpClient } from '@angular/common/http';
import { Injectable, inject, isDevMode } from '@angular/core';
import {
  collectionData,
  collectionGroup,
  Firestore,
  limit,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import {
  type BulkActionAdditionalJobData,
  ExecutionStatus,
  JOB_TYPE_DISPLAY_NAMES,
  type JobExecutionStatus,
  type JobType,
} from '@cdw-ae/common-data';
import * as JobsStatusActions from '@cdw-ae/jobs-status-state';
import {
  buildSsrmLockStorageKey,
  findHydratedLockByJobId,
  FIRESTORE_EXECUTIONS_COLLECTION,
  filterAllowedWorkflows,
  type HydratedLock,
  jobExecutionStatusConverter,
  MAX_JOBS_PER_TAB,
  selectHydratedLocks,
  TRACKED_WORKFLOW_NAMES,
} from '@cdw-ae/jobs-status-state';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concat,
  EMPTY,
  filter,
  fromEvent,
  interval,
  map,
  merge,
  mergeMap,
  of,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  withLatestFrom,
} from 'rxjs';
import { ApiUrlBuilderService } from '../../common/services/api-url-builder.service';
import * as AppLifecyclesActions from '../app-lifecycles/app-lifecycles.actions';
import {
  selectCustomerId,
  selectDbUserId,
} from '../user-identity/user-identity-settings.selectors';

@Injectable()
export class JobsStatusEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private firestore = inject(Firestore);
  private http = inject(HttpClient);
  private urlBuilder = inject(ApiUrlBuilderService);
  private snackbarService = inject(SnackbarService);

  /**
   * Convert Firestore query results to JobExecutionStatus objects
   */
  private convertFirestoreResults(
    results: Record<string, unknown>[]
  ): JobExecutionStatus[] {
    return results.map((doc) =>
      jobExecutionStatusConverter(
        doc as Record<string, unknown>,
        doc.executionId as string
      )
    );
  }


  /**
   * Build Firestore queries for each tracked workflow with specific execution status
   */
  private buildWorkflowQueries(
    executionsRef: ReturnType<typeof collectionGroup>,
    customerId: number,
    status: ExecutionStatus
  ) {
    return TRACKED_WORKFLOW_NAMES.map((workflowName) =>
      query(
        executionsRef,
        where('customerId', '==', customerId),
        where('workflowName', '==', workflowName),
        where('executionStatus', '==', status),
        orderBy('dt', 'desc'),
        limit(1)
      )
    );
  }

  /**
   * Bind to Firestore collection group query after authentication completes
   * Queries all 'executions' subcollections for active jobs matching the customer
   * Also triggers loading of completed jobs from SQL
   */
  bindAfterAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppLifecyclesActions.authCompleted),
      withLatestFrom(
        this.store.select(selectCustomerId),
        this.store.select(selectDbUserId)
      ),
      filter(([, customerId, dbUserId]) => !!customerId && !!dbUserId),
      mergeMap(([, customerId, dbUserId]) => {
        const parsedCustomerId = parseInt(customerId, 10);
        return [
          JobsStatusActions.bindToFirestore({
            customerId: parsedCustomerId,
          }),
          JobsStatusActions.bindToMyJobs({
            customerId: parsedCustomerId,
            dbUserId,
          }),
          JobsStatusActions.bindToAllJobs({
            customerId: parsedCustomerId,
          }),
          JobsStatusActions.loadUserExecutions({
            customerId: parsedCustomerId,
            dbUserId,
            limit: MAX_JOBS_PER_TAB,
          }),
          JobsStatusActions.loadAllExecutions({
            customerId: parsedCustomerId,
            limit: MAX_JOBS_PER_TAB,
          }),
          // Load cache refresh workflow status from SQL
          JobsStatusActions.loadWorkflowExecutions({
            customerId: parsedCustomerId,
            workflowName: 'full-asset-sync-workflow',
            limit: 1,
          }),
        ];
      })
    )
  );

  /**
   * Bind to "My Jobs" - jobs created by the current user
   * Queries all executions where createdByUserId matches the current user
   * Sorts by dt (most recent first)
   *
   * Note: Old jobs without createdByUserId will not appear in My Jobs (expected behavior)
   */
  bindMyJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.bindToMyJobs),
      switchMap(({ customerId, dbUserId }) => {
        const executionsRef = collectionGroup(
          this.firestore,
          FIRESTORE_EXECUTIONS_COLLECTION
        );

        // Query for jobs created by this user
        // Old jobs without createdByUserId will be excluded (expected behavior for user-specific view)
        const myJobsQuery = query(
          executionsRef,
          where('customerId', '==', customerId),
          where('createdByUserId', '==', dbUserId),
          orderBy('dt', 'desc'),
          limit(MAX_JOBS_PER_TAB)
        );

        return collectionData(myJobsQuery, { idField: 'executionId' }).pipe(
          map((results) => {
            // Filter out unwanted workflows (cache refresh jobs, etc.)
            const allowedResults = filterAllowedWorkflows(
              results as Record<string, unknown>[]
            );
            const executions = this.convertFirestoreResults(allowedResults);
            return JobsStatusActions.myJobsUpdated({ executions });
          }),
          catchError((error: unknown) => {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to bind to My Jobs';
            return of(JobsStatusActions.bindingError({ error: errorMessage }));
          }),
          takeUntil(
            this.actions$.pipe(ofType(JobsStatusActions.unbindFromFirestore))
          )
        );
      })
    )
  );

  /**
   * Bind to "All Jobs" - all jobs visible to the current user
   * Queries all executions for this customer
   * Sorts by dt (most recent first)
   *
   * Note: Shows both old jobs (without user metadata) and new jobs (with user metadata)
   */
  bindAllJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.bindToAllJobs),
      switchMap(({ customerId }) => {
        const executionsRef = collectionGroup(
          this.firestore,
          FIRESTORE_EXECUTIONS_COLLECTION
        );

        // Query for all jobs for this customer (includes old jobs without user metadata)
        const allJobsQuery = query(
          executionsRef,
          where('customerId', '==', customerId),
          orderBy('dt', 'desc'),
          limit(MAX_JOBS_PER_TAB)
        );

        return collectionData(allJobsQuery, { idField: 'executionId' }).pipe(
          map((results) => {
            // Filter out unwanted workflows (cache refresh jobs, etc.)
            const allowedResults = filterAllowedWorkflows(
              results as Record<string, unknown>[]
            );
            const executions = this.convertFirestoreResults(allowedResults);
            return JobsStatusActions.allJobsUpdated({ executions });
          }),
          catchError((error: unknown) => {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to bind to All Jobs';
            return of(JobsStatusActions.bindingError({ error: errorMessage }));
          }),
          takeUntil(
            this.actions$.pipe(ofType(JobsStatusActions.unbindFromFirestore))
          )
        );
      })
    )
  );

  /**
   * Execute Firestore collection group queries when binding is requested
   *
   * WORKFLOW TRACKING (not individual jobs):
   * - Queries by workflowName to monitor ALL executions of specific workflow types
   * - Examples: 'full-asset-sync-workflow' for cache refresh status
   * - Active query: customerId + workflowName + executionStatus = 'started' (most recent per workflow)
   * - Use case: Show "Cache refreshing..." indicator; disappearance triggers SQL refetch
   *   for "Cache last updated" timestamp via refetchOnCacheRefreshCompletion$
   */
  bindActiveExecutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.bindToFirestore),
      switchMap(({ customerId }) => {
        const executionsRef = collectionGroup(
          this.firestore,
          FIRESTORE_EXECUTIONS_COLLECTION
        );

        // Query for active (started) executions
        const workflowQueries = this.buildWorkflowQueries(
          executionsRef,
          customerId,
          ExecutionStatus.started
        );

        // Combine all workflow queries
        const queryObservables = workflowQueries.map((q) =>
          collectionData(q, { idField: 'executionId' })
        );

        return combineLatest(queryObservables).pipe(
          map((results) => {
            // Flatten results from all workflows
            const allDocs = results.flat() as Record<string, unknown>[];
            const executions = this.convertFirestoreResults(allDocs);
            return JobsStatusActions.activeExecutionsUpdated({ executions });
          }),
          catchError((error: unknown) => {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to bind to Firestore (active)';
            return of(JobsStatusActions.bindingError({ error: errorMessage }));
          }),
          takeUntil(
            this.actions$.pipe(ofType(JobsStatusActions.unbindFromFirestore))
          )
        );
      })
    )
  );

  /**
   * Re-fetch completed jobs from SQL when a tracked workflow (cache refresh)
   * disappears from active executions. This ensures the "Cache last updated"
   * timestamp refreshes after a cache refresh completes.
   *
   * Under the two-tier storage model, finished executions are deleted from
   * Firestore and persisted to SQL. The active executions Firestore query
   * detects the disappearance, and this effect triggers the SQL refetch.
   */
  refetchOnCacheRefreshCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.activeExecutionsUpdated),
      map((action) => action.executions.map((e) => e.workflowName)),
      startWith([] as string[]),
      pairwise(),
      withLatestFrom(
        this.store.select(selectCustomerId),
        this.store.select(selectDbUserId)
      ),
      filter(([[prevNames, currentNames], customerId, dbUserId]) => {
        const disappeared = prevNames.filter(
          (name: string) => !currentNames.includes(name)
        );
        return disappeared.length > 0 && !!customerId && !!dbUserId;
      }),
      mergeMap(([, customerId, dbUserId]) => [
        JobsStatusActions.loadAllExecutions({
          customerId: parseInt(customerId, 10),
          limit: MAX_JOBS_PER_TAB,
        }),
        JobsStatusActions.loadUserExecutions({
          customerId: parseInt(customerId, 10),
          dbUserId,
          limit: MAX_JOBS_PER_TAB,
        }),
      ])
    )
  );

  /**
   * Load completed jobs for current user from SQL (with Redis caching)
   * One-time fetch on auth completion, not real-time
   */
  loadUserExecutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.loadUserExecutions),
      switchMap(({ customerId, dbUserId, limit }) => {
        const url = this.urlBuilder.backendApi('job-executions', 'user');

        return this.http
          .get<{ executions: JobExecutionStatus[] }>(url, {
            params: {
              dbUserId: dbUserId.toString(),
              limit: (limit ?? MAX_JOBS_PER_TAB).toString(),
            },
          })
          .pipe(
            map((response) => {
              // Convert date strings to Date objects (HTTP serializes dates as strings)
              const executions = response.executions.map((exec) => ({
                ...exec,
                dt: exec.dt ? new Date(exec.dt) : exec.dt,
                createdAt: exec.createdAt
                  ? new Date(exec.createdAt)
                  : exec.createdAt,
              }));
              
              return JobsStatusActions.userExecutionsLoaded({
                executions,
              });
            }),
            catchError((error: unknown) => {
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : 'Failed to load completed jobs';
              return of(
                JobsStatusActions.userExecutionsError({ error: errorMessage })
              );
            })
          );
      })
    )
  );

  /**
   * Load all completed jobs for customer from SQL (with Redis caching)
   * One-time fetch on auth completion, not real-time
   */
  loadAllExecutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.loadAllExecutions),
      switchMap(({ customerId, limit }) => {
        const url = this.urlBuilder.backendApi('job-executions', 'all');

        return this.http
          .get<{ executions: JobExecutionStatus[] }>(url, {
            params: {
              limit: (limit ?? MAX_JOBS_PER_TAB).toString(),
            },
          })
          .pipe(
            map((response) => {
              // Convert date strings to Date objects (HTTP serializes dates as strings)
              const executions = response.executions.map((exec) => ({
                ...exec,
                dt: exec.dt ? new Date(exec.dt) : exec.dt,
                createdAt: exec.createdAt
                  ? new Date(exec.createdAt)
                  : exec.createdAt,
              }));
              return JobsStatusActions.allExecutionsLoaded({
                executions,
              });
            }),
            catchError((error: unknown) => {
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : 'Failed to load all completed jobs';
              return of(
                JobsStatusActions.allExecutionsError({
                  error: errorMessage,
                })
              );
            })
          );
      })
    )
  );

  /**
   * Re-fetch user completed jobs from SQL when jobs disappear from Firestore.
   * Also dispatches jobCompleted for each disappeared job so other effects
   * (e.g. toast notifications) can react.
   */
  refetchUserJobsOnCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.myJobsUpdated),
      map((action) => action.executions),
      startWith([] as JobExecutionStatus[]),
      pairwise(),
      withLatestFrom(
        this.store.select(selectCustomerId),
        this.store.select(selectDbUserId)
      ),
      filter(([[prevExecs, currentExecs], customerId, dbUserId]) => {
        const currentIds = new Set(currentExecs.map((e) => e.jobId));
        const disappeared = prevExecs.filter((e) => !currentIds.has(e.jobId));
        return disappeared.length > 0 && !!customerId && !!dbUserId;
      }),
      mergeMap(([[prevExecs, currentExecs], customerId, dbUserId]) => {
        const currentIds = new Set(currentExecs.map((e) => e.jobId));
        const disappeared = prevExecs.filter((e) => !currentIds.has(e.jobId));

        return [
          JobsStatusActions.loadUserExecutions({
            customerId: parseInt(customerId, 10),
            dbUserId,
            limit: MAX_JOBS_PER_TAB,
          }),
          ...disappeared.map((execution) =>
            JobsStatusActions.jobCompleted({ execution })
          ),
        ];
      })
    )
  );

  /**
   * Re-fetch all completed jobs from SQL when jobs disappear from Firestore
   * This ensures completed jobs appear after Firestore deletion
   */
  refetchAllJobsOnCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.allJobsUpdated),
      map((action) => action.executions.map((e) => e.jobId)),
      startWith([] as number[]),
      pairwise(),
      withLatestFrom(
        this.store.select(selectCustomerId),
        this.store.select(selectDbUserId)
      ),
      filter(([[prevJobIds, currentJobIds], customerId, dbUserId]) => {
        const disappearedJobs = prevJobIds.filter(
          (id: number) => !currentJobIds.includes(id)
        );
        return disappearedJobs.length > 0 && !!customerId && !!dbUserId;
      }),
      mergeMap(([[prevJobIds, currentJobIds], customerId, dbUserId]) => {
        prevJobIds.filter(
          (id: number) => !currentJobIds.includes(id)
        );

        return [
          JobsStatusActions.loadAllExecutions({
            customerId: parseInt(customerId, 10),
            limit: MAX_JOBS_PER_TAB,
          }),
          JobsStatusActions.loadUserExecutions({
            customerId: parseInt(customerId, 10),
            dbUserId,
            limit: MAX_JOBS_PER_TAB,
          }),
        ];
      })
    )
  );

  /**
   * Show toast notification when any job completes.
   * Export jobs show a persistent notification directing users to the Jobs panel.
   * All other job types get a simple success/error message.
   * User-cancelled jobs get no toast — the user already knows they cancelled,
   * and the Jobs panel reflects the cancelled status.
   */
  jobCompletedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JobsStatusActions.jobCompleted),
        tap(({ execution }) => {
          

          const status = execution.executionStatus;
          const jobType = execution.additionalJobData?.jobType as
            | JobType
            | undefined;
          const displayName = jobType
            ? JOB_TYPE_DISPLAY_NAMES[jobType]
            : 'Job';

          // User-initiated cancel — stay silent. Showing "completed
          // successfully" here was misleading users into thinking their
          // cancelled import had actually run.
          if (
            status === ExecutionStatus.cancelledRunning ||
            status === ExecutionStatus.cancelledPending
          ) {
            return;
          }

          if (
            status === ExecutionStatus.error ||
            status === ExecutionStatus.failed
          ) {
            this.snackbarService.showError(
              `${displayName} failed.`,
              'Dismiss',
              { duration: 10000, horizontalPosition: 'center' }
            );
          } else if (jobType === 'export') {
            const format = execution.additionalJobData?.format as string;
            const message =
              format === 'google-sheets'
                ? `${displayName} completed. See Jobs panel to view.`
                : `${displayName} completed. See Jobs panel to download.`;
            this.snackbarService.showMessage(message, 'Dismiss', {
              duration: 0,
              horizontalPosition: 'center',
            });
          } else {
            this.snackbarService.showMessage(
              `${displayName} completed successfully.`,
              'Close',
              { duration: 3000, horizontalPosition: 'center' }
            );
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle export download requests.
   * Calls the backend to get an authenticated download URL, then opens it.
   */
  downloadExport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JobsStatusActions.downloadExport),
        mergeMap(({ jobId }) => {
          const url = this.urlBuilder.backendApi(
            'exports',
            'download-url/:jobId',
            { jobId }
          );
          return this.http
            .get<{ url: string; expires: string | null }>(url)
            .pipe(
              tap((response) => window.open(response.url, '_blank')),
              catchError(() => {
                this.snackbarService.showError(
                  'Unable to download export.',
                  'Dismiss'
                );
                return of(null);
              })
            );
        })
      ),
    { dispatch: false }
  );

  /**
   * Load workflow-specific executions from SQL (for cache refresh status, etc.)
   * One-time fetch, not real-time
   */
  loadWorkflowExecutions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.loadWorkflowExecutions),
      switchMap(({ customerId, workflowName, limit }) => {
        const url = this.urlBuilder.backendApi('job-executions', 'all');

        return this.http
          .get<{ executions: JobExecutionStatus[] }>(url, {
            params: {
              limit: (limit ?? 1).toString(),
              workflowName,
            },
          })
          .pipe(
            map((response) => {
              const executions = response.executions.map((exec) => ({
                ...exec,
                dt: exec.dt ? new Date(exec.dt) : exec.dt,
                createdAt: exec.createdAt
                  ? new Date(exec.createdAt)
                  : exec.createdAt,
              }));
              return JobsStatusActions.workflowExecutionsLoaded({
                workflowName,
                executions,
              });
            }),
            catchError((error: unknown) => {
              const errorMessage =
                error instanceof Error
                  ? error.message
                  : 'Failed to load workflow executions';
              return of(
                JobsStatusActions.workflowExecutionsError({
                  workflowName,
                  error: errorMessage,
                })
              );
            })
          );
      })
    )
  );

  /**
   * Re-fetch workflow executions when cache refresh completes
   * Detects when full-asset-sync-workflow disappears from active executions
   */
  refetchWorkflowOnCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.activeExecutionsUpdated),
      map((action) =>
        action.executions
          .filter((e) => e.workflowName === 'full-asset-sync-workflow')
          .map((e) => e.executionId)
      ),
      startWith([] as string[]),
      pairwise(),
      withLatestFrom(this.store.select(selectCustomerId)),
      filter(([[prevIds, currentIds], customerId]) => {
        // Cache refresh was running but now disappeared
        const wasRunning = prevIds.length > 0;
        const notRunningAnymore = currentIds.length === 0;
        return wasRunning && notRunningAnymore && !!customerId;
      }),
      map(([, customerId]) =>
        JobsStatusActions.loadWorkflowExecutions({
          customerId: parseInt(customerId, 10),
          workflowName: 'full-asset-sync-workflow',
          limit: 1,
        })
      )
    )
  );

  /**
   * Unbind from Firestore when user logs out
   */
  unbindOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppLifecyclesActions.initiateLogout),
      map(() => JobsStatusActions.unbindFromFirestore())
    )
  );

  // ==========================================================================
  // SSRM grid selection-lock failsafe effects (fix/SSRM-row-activity-ui)
  //
  // Coordination contract with the device-search consumer:
  //   - bulkJobEnqueued is dispatched after a successful enqueue → persistLock$
  //     writes sessionStorage so same-tab refresh restores the lock.
  //   - lockHydratedFromStorage is dispatched on app init from the consumer
  //     (synchronously, before Firestore listener binds) → reducer marks lock
  //     active pessimistically.
  //   - cancelJobRequested is dispatched by the banner OR the JobCardComponent
  //     cancel button → cancelJob$ fires the API, optimistically releases
  //     local state via selectionLockReleaseRequested.
  //   - reconcileStuckJobs is dispatched by the visibility/online/threshold
  //     trigger → loadUserExecutions re-fetches SQL terminal-state. The
  //     existing refetchUserJobsOnCompletion$ already handles converting
  //     "job disappeared from Firestore" into jobCompleted dispatches; that
  //     plus the selectionLockReleaseRequested reducer rule wraps up cleanly.
  // ==========================================================================

  /**
   * Drives `selectStuckJobsForGridSession` by ticking nowMs every 30s.
   * Always runs (not gated on in-flight jobs) — the cost is one reducer pass
   * every 30s with no downstream subscribers if no jobs are active. Gating
   * adds complexity (start/stop subscriptions on job set changes) for
   * negligible savings.
   */
  nowTick$ = createEffect(() =>
    interval(30_000).pipe(
      map(() => JobsStatusActions.nowTick({ nowMs: Date.now() }))
    )
  );

  /**
   * Visibility/online-driven reconciliation. When the user returns to a
   * backgrounded tab or the network reconnects, re-fetch the SQL terminal-
   * state so any jobs that finished while we weren't listening get released
   * quietly via the existing refetchUserJobsOnCompletion$ path.
   *
   * `throttleTime(2000)` prevents storm dispatches if the user rapidly
   * toggles tabs or the browser fires online events repeatedly during a
   * flaky network. 2s is short enough to feel responsive but long enough
   * to coalesce bursts.
   *
   * The reconcileStuckJobs action is published as a discrete event AND
   * fans out to loadUserExecutions so consumers listening for "we just
   * reconciled" can react if needed.
   */
  reconcileOnVisibilityOrOnline$ = createEffect(() =>
    merge(
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => !document.hidden)
      ),
      fromEvent(window, 'online')
    ).pipe(
      throttleTime(2000),
      map(() => JobsStatusActions.reconcileStuckJobs())
    )
  );

  /**
   * On reconcileStuckJobs, fan out to loadUserExecutions so SQL terminal
   * state arrives. The existing refetchUserJobsOnCompletion$ effect handles
   * the diff (Firestore "disappeared" jobs) and dispatches jobCompleted
   * which the reducer already handles. We don't need a custom "quiet
   * release" path — the existing infra plus the
   * selectionLockReleaseRequested handler do the job.
   */
  reconcileTriggersSqlRefetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.reconcileStuckJobs),
      withLatestFrom(
        this.store.select(selectCustomerId),
        this.store.select(selectDbUserId)
      ),
      filter(([, customerId, dbUserId]) => !!customerId && !!dbUserId),
      map(([, customerId, dbUserId]) =>
        JobsStatusActions.loadUserExecutions({
          customerId: parseInt(customerId, 10),
          dbUserId,
          limit: MAX_JOBS_PER_TAB,
        })
      )
    )
  );

  /**
   * Optimistic cancel: emit selectionLockReleaseRequested FIRST so the UI
   * clears instantly, then fire the HTTP call. If the API fails the lock
   * STAYS released (the user explicitly chose to escape) but a snackbar
   * surfaces the failure so they can refresh-to-verify.
   *
   * Uses concat to guarantee order: release event first, then the HTTP
   * outcome action. mergeMap on the outer enables multiple concurrent
   * cancels (rare, but possible if user mashes buttons across multiple
   * stuck jobs).
   */
  cancelJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.cancelJobRequested),
      mergeMap(({ jobId, customerId, gridSessionUuid }) => {
        const url = this.urlBuilder.backendApi(
          'job-executions',
          `${jobId}/cancel-running`
        );
        const httpCall$ = this.http
          .post<{ success: boolean }>(url, {})
          .pipe(
            map(() => JobsStatusActions.cancelJobSucceeded({ jobId })),
            catchError((error: unknown) => {
              const message =
                error instanceof Error ? error.message : String(error);
              return of(
                JobsStatusActions.cancelJobFailed({ jobId, error: message })
              );
            })
          );
        // Optimistic release: emit before the HTTP call resolves.
        return concat(
          gridSessionUuid
            ? of(
                JobsStatusActions.selectionLockReleaseRequested({
                  gridSessionUuid,
                })
              )
            : EMPTY,
          httpCall$
        );
      })
    )
  );

  /**
   * Surface cancel outcome via snackbar. Success: short confirmation.
   * Failure: tell the user the request didn't reach the server but the
   * local lock has been released — they can refresh-to-verify the actual
   * job state.
   */
  cancelJobSnackbars$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          JobsStatusActions.cancelJobSucceeded,
          JobsStatusActions.cancelJobFailed
        ),
        tap((action) => {
          if (action.type === JobsStatusActions.cancelJobSucceeded.type) {
            this.snackbarService.showMessage('Job cancelled.', '', {
              duration: 4000,
            });
          } else {
            this.snackbarService.showError(
              'Cancel request failed; the job may still be running. Refresh to check status.',
              '',
              { duration: 8000 }
            );
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Persist the lock to sessionStorage when a bulk job is enqueued so the
   * lock survives same-tab refresh. Clear it on release. Storage key shape
   * is centralized via `buildSsrmLockStorageKey()` — writers and the
   * hydration site in DeviceSearchComponent share the same builder so a
   * key-format drift cannot strand entries.
   */
  persistLockToSessionStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JobsStatusActions.bulkJobEnqueued),
        tap(({ jobId, gridSessionUuid, selectedCount }) => {
          try {
            sessionStorage.setItem(
              buildSsrmLockStorageKey(gridSessionUuid),
              JSON.stringify({
                jobId,
                selectedCount,
                lockSetAt: Date.now(),
              })
            );
          } catch {
            // Quota exceeded or storage disabled: degrade silently. The
            // failsafe still works in the current session because the
            // reducer state is also updated by bulkJobEnqueued via
            // hydratedLocks. Only same-tab refresh recovery is affected.
          }
        })
      ),
    { dispatch: false }
  );

  clearLockFromSessionStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        // Both the optimistic release (cancel) and the terminal confirmation
        // end the lock's lifecycle for this session — sessionStorage must be
        // cleared either way so a same-tab refresh doesn't re-hydrate a stale
        // entry. Listening to both keeps the side-effect symmetric.
        ofType(
          JobsStatusActions.selectionLockReleaseRequested,
          JobsStatusActions.selectionLockReleaseConfirmed
        ),
        tap(({ gridSessionUuid }) => {
          try {
            sessionStorage.removeItem(buildSsrmLockStorageKey(gridSessionUuid));
          } catch {
            // Same as above — silent degradation.
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * When Firestore terminal-state arrives for a job we've been tracking,
   * confirm the lock release. Two paths can identify the session:
   *
   *   1. PREFERRED — `execution.additionalJobData.gridSessionUuid` written
   *      by saas-device-api when the X_CDW_GRID_SESSION header survives
   *      the proxy chain. Only the SSRM `useSelection=true` dispatch path
   *      forwards it reliably; the legacy per-row `updateDevices` path
   *      drops it at asset-service-layer's `selectionEnabled` guard.
   *
   *   2. FALLBACK — match by `jobId` against the locally-tracked
   *      `hydratedLocks`. Each tab's hydratedLocks only contains jobs THAT
   *      tab enqueued (single-writer contract), so a jobId match is
   *      unambiguous even when the server lost the gridSessionUuid.
   */
  releaseLockOnJobCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsStatusActions.jobCompleted),
      withLatestFrom(this.store.select(selectHydratedLocks)),
      mergeMap(([{ execution }, hydratedLocks]) => {
        const resolved = resolveSessionFromCompletion(execution, hydratedLocks);
        if (resolved) {
          return [
            JobsStatusActions.selectionLockReleaseConfirmed({
              gridSessionUuid: resolved,
            }),
          ];
        }
        // Lock was hydrated but no jobId match — distinguishes "expected
        // silent drop" (no locks → background job from another tab) from
        // "we couldn't reconcile a lock we OWN". Telemetry is dev-mode
        // only to keep prod consoles clean — known false positive:
        // cancel-then-re-enqueue for the same gridSessionUuid before the
        // original job's terminal arrives. The re-enqueue overwrites
        // hydratedLocks[X] with the new jobId, so when the OLD job's
        // terminal arrives this lookup misses and logs as "stranded" —
        // but there's nothing actually stranded.
        if (isDevMode() && Object.keys(hydratedLocks).length > 0) {
          // biome-ignore lint/suspicious/noConsole: dev-mode telemetry for stranded-lock detection
          console.error(
            'jobs-status: jobCompleted with no gridSessionUuid AND no matching hydrated lock; sessionStorage lock entry may be stranded',
            {
              jobId: execution.jobId,
              hydratedLockJobIds: Object.values(hydratedLocks).map(
                (l) => l.jobId
              ),
            }
          );
        }
        return [];
      })
    )
  );
}

/**
 * Pure helper that maps a completed execution to its owning grid-session.
 * Extracted so the dispatch decision in `releaseLockOnJobCompleted$` is a
 * single-line conditional instead of a 5-path control-flow knot. Surfaces a
 * dev-mode warning if the server payload arrives malformed — falls through
 * to the jobId match either way.
 *
 * Returns the uuid string when matched, or `undefined` when neither path
 * resolves a session (e.g. background jobs another tab enqueued).
 */
export function resolveSessionFromCompletion(
  execution: JobExecutionStatus,
  hydratedLocks: Record<string, HydratedLock>
): string | undefined {
  const typed = execution.additionalJobData as
    | Partial<BulkActionAdditionalJobData>
    | undefined;
  const serverSession = typed?.gridSessionUuid;
  if (typeof serverSession === 'string' && serverSession.length > 0) {
    return serverSession;
  }
  if (
    isDevMode() &&
    serverSession !== undefined &&
    typeof serverSession !== 'string'
  ) {
    // biome-ignore lint/suspicious/noConsole: dev-mode telemetry for corrupt server payload
    console.error(
      'jobs-status: jobCompleted.additionalJobData.gridSessionUuid arrived as non-string; falling through to jobId match',
      { receivedType: typeof serverSession, jobId: execution.jobId }
    );
  }
  return findHydratedLockByJobId(hydratedLocks, execution.jobId)?.[0];
}
